import axios from "axios";
import api from "./api";

/**
 * System API Service for Developer Master functionality
 * This service handles all API calls related to system-level configuration
 * and management, accessible only to users with the 'developer' role.
 */

// Create a separate axios instance for notification endpoints
// This is needed since these use a different base URL pattern than the main api.js
const notificationApi = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL
    ? `${process.env.REACT_APP_SERVER_URL}/api/v1/system`
    : "/api/v1/system",
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to include auth token in headers
notificationApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["x-auth-token"] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const systemAPI = {
  // Department Management
  getDepartments: () => api.get("/system/departments"),
  createDepartment: (data) => api.post("/system/departments", data),
  updateDepartment: (id, data) => api.put(`/system/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/system/departments/${id}`),

  // Department-specific settings
  getDepartmentSettings: (department) =>
    api.get(`/system/departments/${department}/settings`),
  updateDepartmentSettings: (department, data) =>
    api.put(`/system/departments/${department}/settings`, data),

  // System Settings
  getSystemSettings: () => api.get("/system/settings"),
  updateSystemSettings: (data) => api.put("/system/settings", data),

  // System Config
  getSystemConfig: () => api.get("/system/config"),
  updateSystemConfig: (data) => api.put("/system/config", data),

  // Predefined Data
  getPredefinedCategories: () => api.get("/system/predefined/categories"),
  updatePredefinedCategories: (data) =>
    api.put("/system/predefined/categories", data),
  getPredefinedUnits: () => api.get("/system/predefined/units"),
  updatePredefinedUnits: (data) => api.put("/system/predefined/units", data),

  // System Status
  getSystemStatus: () => api.get("/system/status"),

  // System Logs
  getSystemLogs: async (filter = "all") => {
    try {
      const response = await api.get(`/system/logs?filter=${filter}`);
      return response;
    } catch (error) {
      console.error("Error fetching system logs:", error);
      throw error;
    }
  },

  // Clear system cache
  clearSystemCache: () => api.post("/system/cache/clear"),

  // Admin creation
  createAdmin: (data) => api.post("/system/admins", data),

  // Notification methods (uses the notification-specific API)
  getNotifications: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add filters to the query params
      if (filters.read !== undefined) {
        queryParams.append("read", filters.read);
      }

      const response = await notificationApi.get(
        `/notifications?${queryParams}`
      );
      return response;
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  },

  // Mark specific notification as read
  markNotificationRead: async (notificationId) => {
    try {
      const response = await notificationApi.put(
        `/notifications/${notificationId}/read`
      );
      return response;
    } catch (error) {
      console.error("Error marking notification as read:", error);
      throw error;
    }
  },

  // Mark all notifications as read
  markNotificationsAsRead: async () => {
    try {
      // Fix: Change path to /mark-read to match backend route
      const response = await notificationApi.put(`/notifications/mark-read`);
      return response;
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      throw error;
    }
  },

  // Delete a notification
  deleteNotification: async (notificationId) => {
    try {
      const response = await notificationApi.delete(
        `/notifications/${notificationId}`
      );
      return response;
    } catch (error) {
      console.error("Error deleting notification:", error);
      throw error;
    }
  },

  // Get recent system activities
  getRecentActivities: async (limit = 5) => {
    try {
      const response = await api.get(`/system/activities?limit=${limit}`);
      return response;
    } catch (error) {
      console.error("Error fetching recent activities:", error);
      throw error;
    }
  },
};

export default systemAPI;
